import { useEffect, useRef, useState } from "react";
import styles from "./Dropdown.module.css";

let dropdownIdCounter = 0;

const OptionLeading = ({ option }) => {
  if (option?.icon) {
    return <span className={styles.leading}>{option.icon}</span>;
  }
  if (option?.flag) {
    return (
      <span className={styles.leading}>
        <img className={styles.flag} src={option.flag} alt="" />
      </span>
    );
  }
  return null;
};

/**
 * Canonical single-select dropdown for the product UI.
 * Use this for header language, filters, and form selects — do not fork markup.
 * Options may include `flag` (image URL) or `icon` (React node) shown on the left.
 */
const Dropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "",
  disabled = false,
  className = "",
  menuClassName = "",
  "aria-label": ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  const listboxIdRef = useRef(`akanthas-dropdown-${++dropdownIdCounter}`);
  const listboxId = listboxIdRef.current;

  const selectedOption = options.find(
    (option) => String(option.value) === String(value),
  );
  const triggerLabel = selectedOption?.label || placeholder || "";

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const selectOption = (option) => {
    if (disabled || option.disabled) return;
    onChange?.(option.value, option);
    setIsOpen(false);
  };

  return (
    <div
      className={[styles.root, className].filter(Boolean).join(" ")}
      ref={rootRef}
    >
      <button
        type="button"
        className={[styles.trigger, isOpen ? styles.triggerOpen : ""]
          .filter(Boolean)
          .join(" ")}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => {
          if (!disabled) setIsOpen((open) => !open);
        }}
      >
        <span className={styles.triggerContent}>
          <OptionLeading option={selectedOption} />
          <span className={styles.triggerLabel}>{triggerLabel}</span>
        </span>
        <i
          className={`fas fa-chevron-${isOpen ? "up" : "down"} ${styles.chevron}`}
          aria-hidden
        />
      </button>

      {isOpen ? (
        <div
          id={listboxId}
          className={[styles.menu, menuClassName].filter(Boolean).join(" ")}
          role="listbox"
        >
          {options.map((option) => {
            const selected = String(option.value) === String(value);
            return (
              <button
                key={String(option.value)}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={option.disabled}
                className={[styles.item, selected ? styles.itemSelected : ""]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => selectOption(option)}
              >
                <OptionLeading option={option} />
                <span className={styles.itemLabel}>{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default Dropdown;
